import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import { FaDatabase, FaCommentDots, FaImage, FaCheck, FaExpandAlt, FaCompressAlt, FaEdit } from 'react-icons/fa';
import { Card, Badge, Button, Accordion, Row, Col, Image } from 'react-bootstrap';
import 'react-toastify/dist/ReactToastify.css';

const STORE_API_URL = process.env.REACT_APP_API_URL;
const ELECTRON_URL = process.env.REACT_APP_ELECTRON_URL;

function SuperAdmin() {
  const { token } = useContext(AuthContext); // Access user authentication
  const [loading, setLoading] = useState(false);
  const [storeChanges, setStoreChanges] = useState([]);
  const [productChanges, setProductChanges] = useState([]);
  const [groupedProductChanges, setGroupedProductChanges] = useState({});
  const [expandedProductIds, setExpandedProductIds] = useState({});
  const [stores, setStores] = useState([]);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [submissionFields, setSubmissionFields] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchPendingAuditLogs();
      fetchStores();
      fetchPendingSubmissions();
    }
  }, []);

  // Fetch stores for orphaned images dropdown
  const fetchStores = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/stores`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stores.');
      }

      const data = await response.json();
      setStores(data);
    } catch (error) {
      toast.error(`Error fetching stores: ${error.message}`);
    }
  };

  // Fetch pending audit logs and separate them by type
  const fetchPendingAuditLogs = async () => {
    setLoading(true);
    try {
      // Request store changes (ungrouped)
      const storeResponse = await fetch(`${STORE_API_URL}/super/auditlogs?status=pending&type=store`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Request product changes (grouped by product_id)
      const productResponse = await fetch(`${STORE_API_URL}/super/auditlogs?status=pending&type=product&groupBy=product_id`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!storeResponse.ok || !productResponse.ok) {
        throw new Error('Failed to fetch pending audit logs.');
      }

      const storeData = await storeResponse.json();
      const productData = await productResponse.json();

      setStoreChanges(storeData);
      
      // If the backend returned already grouped data, use it directly
      if (productData.grouped && Array.isArray(productData.items)) {
        setProductChanges(productData.items.flatMap(group => group.changes)); // Keep existing state structure
        setGroupedProductChanges(productData.items.reduce((acc, group) => {
          acc[group.productId] = group;
          return acc;
        }, {}));
      } else {
        // Fallback to client-side grouping if backend didn't return grouped data
        const productChanges = productData;
        setProductChanges(productChanges);
        
        // Group product changes by product_id
        const groupedProducts = productChanges.reduce((acc, change) => {
          const productId = change.product_id;
          if (!acc[productId]) {
            acc[productId] = {
              productId: productId,
              productName: change.product_name || 'Unknown Product',
              storeHandle: change.store_handle,
              storeId: change.store_id,
              changes: [],
              changeIds: [],
              allImageFields: {}
            };
          }
          
          acc[productId].changes.push(change);
          acc[productId].changeIds.push(change._id);
          
          // Extract image fields from change details
          const imageFields = extractImageFields(change);
          acc[productId].allImageFields = { ...acc[productId].allImageFields, ...imageFields };
          
          return acc;
        }, {});
        
        setGroupedProductChanges(groupedProducts);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Mark audit log as reviewed
  const handleReviewChanges = async (changeId) => {
    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/super/auditlogs`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ changeIds: Array.isArray(changeId) ? changeId : [changeId] }),
      });

      if (!response.ok) {
        throw new Error('Failed to update changes.');
      }

      const data = await response.json();
      toast.success(data.message);
      fetchPendingAuditLogs(); // Refresh the list
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Toggle expanded state for product details
  const toggleProductExpanded = (productId) => {
    setExpandedProductIds(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Check if a field might contain an image path
  const isImageField = (fieldName, value) => {
    if (typeof value !== 'string') return false;
    
    // Check field name against common image field patterns
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const imageFieldNames = [
      'image', 'photo', 'picture', 'thumbnail', 'cover', 'logo', 'banner', 
      'product_image', 'main_image', 'featured_image', 'icon', 'avatar',
      'gallery', 'media', 'pic', 'thumb'
    ];
    
    const isImageFieldName = imageFieldNames.some(
      imgField => fieldName.toLowerCase().includes(imgField)
    );
    
    // Check if the value contains an image extension
    const hasImageExtension = imageExtensions.some(ext => value.toLowerCase().includes(ext));
    
    // Check if the value is a typical image URL
    const hasImageUrl = value.includes('http') && 
                      (value.includes('/images/') || 
                       value.includes('/products/') || 
                       value.includes('/assets/') ||
                       value.includes('/uploads/') ||
                       value.includes('/media/') ||
                       value.includes('digitaloceanspaces.com') || 
                       value.includes('amazonaws.com') ||
                       value.includes('cloudinary.com') ||
                       value.includes('cloudfront.net'));
    
    // If the field name suggests it's an image, be more lenient with URL detection
    if (isImageFieldName) {
      return hasImageExtension || hasImageUrl || value.includes('http');
    }
    
    // Otherwise require stronger evidence
    return (hasImageExtension && hasImageUrl) || 
           (hasImageExtension && value.startsWith('http')) ||
           (hasImageUrl && value.length > 20);
  };

  // Extract image fields from a change object
  const extractImageFields = (change) => {
    const imageFields = {};
    // Keep track of already added image URLs to avoid duplicates
    const addedImageUrls = new Set();
    
    // Helper function to get best available image URL with priority
    const getBestImageUrl = (img) => {
      // Priority: compressed_url > cropped_url > url
      return img.compressed_url || img.cropped_url || img.url;
    };
    
    try {
      // Check directly in imageFields if provided by backend
      if (change.imageFields && typeof change.imageFields === 'object') {
        // Deduplicate image URLs
        const uniqueImages = {};
        Object.entries(change.imageFields).forEach(([field, url]) => {
          if (!addedImageUrls.has(url)) {
            uniqueImages[field] = url;
            addedImageUrls.add(url);
          }
        });
        return uniqueImages;
      }
      
      // PRIORITY 1: First check the 'changes' object directly, which contains only changed fields
      if (change.changes) {
        // Especially look for image arrays where we can calculate the delta
        Object.entries(change.changes).forEach(([key, value]) => {
          // Handle special case of image arrays with old and new values
          if (key === 'images' && value.old && value.new && 
              Array.isArray(value.old) && Array.isArray(value.new)) {
            
            // Map old images by URL for comparison
            const oldImagesMap = new Map();
            value.old.forEach((img, idx) => {
              const url = getBestImageUrl(img);
              if (url) {
                // Use URL as key for comparison
                oldImagesMap.set(url, { img, idx });
              }
            });
            
            // Find new or modified images
            value.new.forEach((img, idx) => {
              const url = getBestImageUrl(img);
              if (!url) return;
              
              // If image doesn't exist in old array or has different properties, it's changed
              const oldImageInfo = oldImagesMap.get(url);
              const isNew = !oldImageInfo;
              
              // Only include if it's a new image or if key properties changed
              if (isNew && !addedImageUrls.has(url)) {
                imageFields[`${key}[${idx}]`] = url;
                addedImageUrls.add(url);
              }
            });
            
            // If we found actual image changes, return early
            if (Object.keys(imageFields).length > 0) {
              return imageFields;
            }
          }
          
          // Handle other types of changes
          if (value && value.new !== undefined) {
            if (typeof value.new === 'string' && isImageField(key, value.new) && !addedImageUrls.has(value.new)) {
              imageFields[key] = value.new;
              addedImageUrls.add(value.new);
            } else if (value.new && typeof value.new === 'object' && Array.isArray(value.new)) {
              // Handle arrays of image objects in changes
              if (value.new.length > 0 && 
                  typeof value.new[0] === 'object' && 
                  (value.new[0].url || value.new[0].cropped_url || value.new[0].compressed_url)) {
                
                // If we have old array for comparison, only include new/changed images
                if (value.old && Array.isArray(value.old)) {
                  // Map old images by URL 
                  const oldUrlMap = new Map();
                  value.old.forEach(oldImg => {
                    const url = getBestImageUrl(oldImg);
                    if (url) oldUrlMap.set(url, oldImg);
                  });
                  
                  // Only include images not in the old array
                  value.new.slice(0, 5).forEach((img, idx) => {
                    const imageUrl = getBestImageUrl(img);
                    if (imageUrl && !oldUrlMap.has(imageUrl) && !addedImageUrls.has(imageUrl)) {
                      imageFields[`${key}[${idx}]`] = imageUrl;
                      addedImageUrls.add(imageUrl);
                    }
                  });
                } else {
                  // No old array - consider all as new
                  value.new.slice(0, 5).forEach((img, idx) => {
                    const imageUrl = getBestImageUrl(img);
                    if (imageUrl && !addedImageUrls.has(imageUrl)) {
                      imageFields[`${key}[${idx}]`] = imageUrl;
                      addedImageUrls.add(imageUrl);
                    }
                  });
                }
              }
            }
          }
        });
        
        // If we found images in the changes object, prefer those and return early
        if (Object.keys(imageFields).length > 0) {
          return imageFields;
        }
      }
      
      // PRIORITY 2: Check if the entire operation is a 'create' action, in which case all fields are new
      const isCreateOperation = 
        change.action === 'create' || 
        change.operation === 'create' || 
        (change.updated_fields && change.updated_fields.length === 0); // No updated fields may indicate a new product
      
      // Try to parse details if it's a string
      let details = change.details;
      if (typeof details === 'string') {
        try {
          if (details.trim().startsWith('{') || details.trim().startsWith('[')) {
            details = JSON.parse(details);
          }
        } catch (e) {
          // Keep details as is if parsing fails
        }
      }
      
      // If this is a create operation, all fields are effectively "changed"
      if (isCreateOperation && details && typeof details === 'object') {
        // Handle arrays of image objects for newly created products
        if (Array.isArray(details) && 
            details.length > 0 && 
            details[0] && 
            typeof details[0] === 'object' && 
            (details[0].url || details[0].cropped_url || details[0].compressed_url)) {
          
          // Extract first 5 images from array to show as thumbnails
          details.slice(0, 5).forEach((img, idx) => {
            const imageUrl = getBestImageUrl(img);
            if (imageUrl && !addedImageUrls.has(imageUrl)) {
              imageFields[`images[${idx}]`] = imageUrl;
              addedImageUrls.add(imageUrl);
            }
          });
          return imageFields;
        }
        
        // Only process specific fields mentioned in updated_fields if available
        const fieldsToCheck = change.updated_fields && change.updated_fields.length > 0 
          ? change.updated_fields 
          : Object.keys(details);
        
        const checkNestedObject = (obj, prefix = '', fields = null) => {
          for (const [key, value] of Object.entries(obj)) {
            const fullKey = prefix ? `${prefix}.${key}` : key;
            
            // Skip fields not in the updated_fields list, unless it's a create operation
            if (fields && !fields.includes(key) && !fields.some(f => f.startsWith(`${key}.`)) && !isCreateOperation) {
              continue;
            }
            
            if (typeof value === 'string' && isImageField(key, value) && !addedImageUrls.has(value)) {
              imageFields[fullKey] = value;
              addedImageUrls.add(value);
            } else if (Array.isArray(value) && 
                      value.length > 0 && 
                      typeof value[0] === 'object' && 
                      (value[0].url || value[0].cropped_url || value[0].compressed_url)) {
              // Handle arrays of image objects inside details
              value.slice(0, 3).forEach((img, idx) => {
                const imageUrl = getBestImageUrl(img);
                if (imageUrl && !addedImageUrls.has(imageUrl)) {
                  imageFields[`${fullKey}[${idx}]`] = imageUrl;
                  addedImageUrls.add(imageUrl);
                }
              });
            } else if (value && typeof value === 'object') {
              checkNestedObject(value, fullKey, null);
            }
          }
        };
        
        checkNestedObject(details, '', fieldsToCheck);
      }
      
      // PRIORITY 3: Check raw text for image URLs as a last resort
      if (Object.keys(imageFields).length === 0 && typeof change.details === 'string') {
        const imageUrlRegex = /(https?:\/\/[^\s"']+\.(jpg|jpeg|png|gif|webp))/gi;
        const matches = change.details.match(imageUrlRegex);
        if (matches) {
          // Remove duplicates by using the URL itself as the key
          const uniqueMatches = [...new Set(matches)];
          uniqueMatches.forEach((url) => {
            // Use a simpler field key based on the URL to better deduplicate
            // For duplicate URLs, this will overwrite the previous one with the same key
            if (!addedImageUrls.has(url)) {
              // Create a simple hash of the URL to use as the field name
              const urlHash = url.split('/').pop().split('?')[0].replace(/[^a-zA-Z0-9]/g, '');
              imageFields[`extracted_${urlHash}`] = url;
              addedImageUrls.add(url);
            }
          });
        }
      }
    } catch (e) {
      console.error('Error extracting image fields:', e);
    }
    
    return imageFields;
  };

  // Extract image URLs from change details
  const extractImageUrls = (change) => {
    const images = [];
    const addedUrls = new Set(); // Track unique URLs
    
    // Helper function to get best available image URL with priority
    const getBestImageUrl = (img) => {
      // Priority: compressed_url > cropped_url > url
      return img.compressed_url || img.cropped_url || img.url;
    };
    
    try {
      // Check if the entire operation is a 'create' action
      const isCreateOperation = 
        change.action === 'create' || 
        change.operation === 'create' || 
        (change.updated_fields && change.updated_fields.length === 0); // No updated fields may indicate a new product
      
      // PRIORITY 1: First try directly using imageFields if provided by backend
      if (change.imageFields && Object.keys(change.imageFields).length > 0) {
        const uniqueImages = [];
        Object.entries(change.imageFields).forEach(([field, url]) => {
          if (!addedUrls.has(url)) {
            uniqueImages.push({ field, url });
            addedUrls.add(url);
          }
        });
        return uniqueImages;
      }
      
      // PRIORITY 2: Check the 'changes' object directly, which contains only changed fields
      if (change.changes) {
        // Handle special case of image arrays
        if (change.changes.images && change.changes.images.old && change.changes.images.new &&
            Array.isArray(change.changes.images.old) && Array.isArray(change.changes.images.new)) {
          
          // Map old images by URL for comparison
          const oldImagesMap = new Map();
          change.changes.images.old.forEach((img, idx) => {
            const url = getBestImageUrl(img);
            if (url) {
              oldImagesMap.set(url, { img, idx });
            }
          });
          
          // Find new or modified images (ones not in the old array)
          change.changes.images.new.forEach((img, idx) => {
            const url = getBestImageUrl(img);
            if (!url) return;
            
            // Only include if it's new (not in old array)
            const oldImageInfo = oldImagesMap.get(url);
            const isNew = !oldImageInfo;
            
            if (isNew && !addedUrls.has(url)) {
              images.push({ field: `images[${idx}]`, url });
              addedUrls.add(url);
            }
          });
          
          // If we found actual changes, return them
          if (images.length > 0) {
            return images;
          }
        }
        
        // Process other changed fields
        for (const [field, value] of Object.entries(change.changes)) {
          if (value && value.new && isImageField(field, value.new) && !addedUrls.has(value.new)) {
            images.push({ field, url: value.new });
            addedUrls.add(value.new);
          } else if (value && value.new && typeof value.new === 'object' && Array.isArray(value.new)) {
            // Handle arrays of image objects
            if (value.new.length > 0 && 
                typeof value.new[0] === 'object' && 
                (value.new[0].url || value.new[0].cropped_url || value.new[0].compressed_url)) {
              
              // If we have old array for comparison, only show new/changed items
              if (value.old && Array.isArray(value.old)) {
                // Map old images by URL for comparison
                const oldImagesMap = new Map();
                value.old.forEach((img) => {
                  const url = getBestImageUrl(img);
                  if (url) {
                    oldImagesMap.set(url, img);
                  }
                });
                
                // Only include images not in the old array
                value.new.forEach((img, idx) => {
                  const imageUrl = getBestImageUrl(img);
                  if (imageUrl && !oldImagesMap.has(imageUrl) && !addedUrls.has(imageUrl)) {
                    images.push({ field: `${field}[${idx}]`, url: imageUrl });
                    addedUrls.add(imageUrl);
                  }
                });
              } else {
                // No old array - consider all as new
                value.new.forEach((img, idx) => {
                  const imageUrl = getBestImageUrl(img);
                  if (imageUrl && !addedUrls.has(imageUrl)) {
                    images.push({ field: `${field}[${idx}]`, url: imageUrl });
                    addedUrls.add(imageUrl);
                  }
                });
              }
            }
          }
        }
        
        // If we found images in changes, return those
        if (images.length > 0) {
          return images;
        }
      }
      
      // PRIORITY 3: Extract from details (only if it's a create operation or we haven't found images yet)
      if (change.details) {
        let details = change.details;
        
        // Parse if string
        if (typeof details === 'string') {
          try {
            if (details.trim().startsWith('{') || details.trim().startsWith('[')) {
              details = JSON.parse(details);
            } else {
              // Check for image URLs in the raw string (only as a last resort)
              if (images.length === 0) {
                const imageUrlRegex = /(https?:\/\/[^\s"',]+\.(jpg|jpeg|png|gif|webp))/gi;
                const matches = details.match(imageUrlRegex);
                if (matches) {
                  const uniqueMatches = [...new Set(matches)]; // Remove duplicates
                  uniqueMatches.forEach((url) => {
                    if (!addedUrls.has(url)) {
                      images.push({ field: 'extracted', url });
                      addedUrls.add(url);
                    }
                  });
                }
              }
            }
          } catch (e) {
            console.error('Error parsing details JSON:', e);
          }
        }
        
        // Process object if available (either parsed or original)
        if (details && typeof details === 'object' && (isCreateOperation || images.length === 0)) {
          // Only process fields that were updated
          const fieldsToCheck = change.updated_fields && change.updated_fields.length > 0 
            ? change.updated_fields 
            : null;
          
          // Check if this is a new record with images
          const processObj = (obj, prefix = '') => {
            for (const [field, value] of Object.entries(obj)) {
              const fullField = prefix ? `${prefix}.${field}` : field;
              
              // Skip fields not in the updated_fields list, unless it's a create operation or no fields specified
              if (fieldsToCheck && !fieldsToCheck.includes(field) && 
                  !fieldsToCheck.some(f => f.startsWith(`${field}.`)) && !isCreateOperation) {
                continue;
              }
              
              if (isImageField(field, value) && !addedUrls.has(value)) {
                images.push({ field: fullField, url: value });
                addedUrls.add(value);
              }
              
              // Handle arrays of image objects
              if (Array.isArray(value) && 
                  value.length > 0 && 
                  typeof value[0] === 'object' && 
                  (value[0].url || value[0].cropped_url || value[0].compressed_url)) {
                
                value.forEach((img, idx) => {
                  const imageUrl = getBestImageUrl(img);
                  if (imageUrl && !addedUrls.has(imageUrl)) {
                    images.push({ field: `${fullField}[${idx}]`, url: imageUrl });
                    addedUrls.add(imageUrl);
                  }
                });
                continue;
              }
              
              // Check nested objects
              if (value && typeof value === 'object') {
                processObj(value, fullField);
              }
            }
          };
          
          processObj(details);
        }
      }
    } catch (e) {
      console.error('Error extracting image URLs:', e);
    }
    
    return images;
  };
  
  // Create a summary of changes
  const createChangeSummary = (changes) => {
    const fields = new Set();
    const actions = new Set();
    const images = [];
    
    changes.forEach(change => {
      // Add fields that were updated
      if (change.updated_fields && Array.isArray(change.updated_fields)) {
        change.updated_fields.forEach(field => fields.add(field));
      }
      
      // Try to extract action from multiple possible fields
      if (change.action) {
        actions.add(change.action);
      } else if (change.operation) {
        actions.add(change.operation);
      } else if (change.type && change.type !== 'product' && change.type !== 'store') {
        // If type is not a generic category but an actual operation
        actions.add(change.type);
      }
      
      // Check if there's an action in the details field
      if (change.parsedDetails && change.parsedDetails.action) {
        actions.add(change.parsedDetails.action);
      }
      
      // If we found no actions, add a default based on updated_fields presence
      if (actions.size === 0 && change.updated_fields && change.updated_fields.length > 0) {
        actions.add('update');
      } else if (actions.size === 0) {
        // Check if it's likely a create or delete operation based on details
        const detailsStr = typeof change.details === 'string' 
          ? change.details 
          : JSON.stringify(change.details || {});
          
        if (detailsStr.includes('created') || detailsStr.includes('new product')) {
          actions.add('create');
        } else if (detailsStr.includes('deleted') || detailsStr.includes('removed')) {
          actions.add('delete');
        } else {
          actions.add('update'); // Default fallback
        }
      }
      
      // Extract images
      images.push(...extractImageUrls(change));
    });
    
    return {
      fields: Array.from(fields),
      actions: Array.from(actions),
      images
    };
  };

  // TODO: This method can be run as a cronjob in the background later
  // For now, we are going to manually update
  const handleSuperExchangeUpdate= async () => {
    setLoading(true);
    try {
      // Example API call for a super admin action
      const response = await fetch(`${STORE_API_URL}/super/exchangeupdate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Exchange update failed');
      }

      const data = await response.json();
      toast.success(`Exchange completed: ${data.message}`);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSitemap = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/super/sitemap.xml`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
  
      if (!response.ok) {
        throw new Error('Failed to generate sitemap');
      }
  
      toast.success('Sitemap.xml successfully generated');
    } catch (error) {
      toast.error(`Error generating sitemap: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCRTDatabase = () => {
    navigate('/superadmin/crts');
  };

  const handleCRTAttributes = () => {
    navigate('/superadmin/crtattributes');
  };

  const handleModBoards = () => {
    navigate('/superadmin/mod-boards');
  };

  const handleCRTDocuments = () => {
    navigate('/superadmin/documents');
  };

  const handleProcessCRTDatabaseAuthors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/super/process-temp-crts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to process CRT Database authors');
      }

      const data = await response.json();
      toast.success(`Processing completed: ${data.results.total_matched} CRTs updated, ${data.results.total_skipped} skipped`);
      
      if (data.results.total_errors > 0) {
        console.error('Processing errors:', data.results.errors);
        toast.warning(`${data.results.total_errors} errors occurred during processing`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add function to handle field selection
  const handleFieldSelection = (submissionId, field, checked) => {
    setSubmissionFields(prev => {
      const currentFields = prev[submissionId] || new Set();
      const newFields = new Set(currentFields);
      
      if (checked) {
        newFields.add(field);
      } else {
        newFields.delete(field);
      }
      
      return {
        ...prev,
        [submissionId]: newFields
      };
    });
  };

  // Add function to initialize field selection based on actual changes
  const initializeFieldSelection = (submission) => {
    if (!submission.target_crt || !submission.crt_data) return;

    const changedFields = new Set();
    Object.entries(submission.crt_data).forEach(([field, newValue]) => {
      const currentValue = submission.target_crt[field];
      const hasChange = JSON.stringify(currentValue) !== JSON.stringify(newValue);
      if (hasChange) {
        changedFields.add(field);
      }
    });

    setSubmissionFields(prev => ({
      ...prev,
      [submission._id]: changedFields
    }));
  };

  // Add this to fetchPendingSubmissions to initialize fields when submissions are loaded
  const fetchPendingSubmissions = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/super/submissions/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pending submissions');
      }

      const data = await response.json();
      setPendingSubmissions(data);
      
      // Initialize field selection for each submission
      data.forEach(submission => {
        initializeFieldSelection(submission);
      });
    } catch (error) {
      toast.error(`Error fetching submissions: ${error.message}`);
    }
  };

  // Update handleSubmissionReview to use the state
  const handleSubmissionReview = async (submission, approved) => {
    try {
      console.log('Reviewing submission:', submission._id);
      const selectedFields = submissionFields[submission._id];
      console.log('Selected fields before sending:', selectedFields);

      const requestBody = { 
        approved,
        selectedFields: approved && selectedFields ? Array.from(selectedFields) : undefined
      };

      console.log('Sending request body:', requestBody);

      const response = await fetch(`${STORE_API_URL}/super/submissions/${submission._id}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to review submission');
      }

      const responseData = await response.json();
      console.log('Review response:', responseData);

      toast.success(`Submission ${approved ? 'approved' : 'rejected'} successfully`);
      fetchPendingSubmissions(); // Refresh the list
    } catch (error) {
      console.error('Error in handleSubmissionReview:', error);
      toast.error(`Error reviewing submission: ${error.message}`);
    }
  };

  const redditLogin = async () => {
    const response = await fetch(`${STORE_API_URL}/reddit/login`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    window.location.href = data.url; 
  }

  return (
    <div className="container">
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">Admin</h2>
      </div>
      
      {/* Add CSS for image hover effects */}
      <style>
        {`
          .preview-card:hover .image-overlay {
            opacity: 1 !important;
          }
          
          .admin-section {
            margin-bottom: 1.5rem;
            padding: 1rem;
            border-radius: 0.5rem;
            background-color: rgba(255, 255, 255, 0.05);
          }
          
          .admin-section h5 {
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }
        `}
      </style>
      
      {/* Organized Admin Functions */}
      <div className="row mb-4">
        {/* Database Management Section */}
        <div className="col-md-6 mb-3">
          <div className="admin-section h-100">
            <h5><i className="fas fa-database me-2"></i>Database Management</h5>
            <div className="d-flex flex-wrap gap-2">
              <button 
                className="btn btn-primary" 
                onClick={handleCRTDatabase}>
                  <FaDatabase style={{ marginRight: '5px' }} />CRT Database
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCRTAttributes}>
                  <FaDatabase style={{ marginRight: '5px' }} />CRT Attributes
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleModBoards}>
                  <FaDatabase style={{ marginRight: '5px' }} />Mod Boards
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCRTDocuments}>
                  <FaDatabase style={{ marginRight: '5px' }} />CRT Documents
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={handleSuperExchangeUpdate}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Update Exchange Products'}
              </button>
              {/* TODO: we can remove this by September 2025
              <button
                className="btn btn-outline-primary"
                onClick={handleProcessCRTDatabaseAuthors}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Process CRT Database Authors'}
              </button>
              */}
            </div>
          </div>
        </div>
        
        {/* Site Management Section */}
        <div className="col-md-6 mb-3">
          <div className="admin-section h-100">
            <h5><i className="fas fa-globe me-2"></i>Site Management</h5>
            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={handleGenerateSitemap}
                disabled={loading}
              >
                {loading ? 'Generating sitemap...' : 'Generate Sitemap.xml'}
              </button>
              <button 
                className="btn btn-outline-primary"
                onClick={() => navigate('/superadmin/orphaned-images')}
              >
                <FaImage style={{ marginRight: '5px' }} />Manage Orphaned Images
              </button>
              <Button
                className="btn btn-outline-primary"
                onClick={() => navigate('/superadmin/ebay-image-extractor')}
              >
                eBay Image Extraction
              </Button>
              <button 
                className="btn btn-outline-primary"
                onClick={() => redditLogin()}
              >
                Reddit Login
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => {throw new Error("This is your first error!");}}
              >
                Sentry Test
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <h2 className="headline">Review Pending Changes</h2>
      

      {/* Add CRT Submissions Section */}
      <div className="admin-section mb-4">
        <h5><FaImage className="me-2" />Pending CRT Submissions</h5>
        {pendingSubmissions.length === 0 ? (
          <p>No pending submissions to review.</p>
        ) : (
          <div className="row">
            {pendingSubmissions.map((submission) => (
              <div key={submission._id} className="col-md-12 col-lg-12 mb-4">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{submission.name || 'Unknown CRT'}</strong>
                      {submission.submission_type === 'contribution' && (
                        <Badge bg="info" className="ms-2">Contribution</Badge>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {/* Submission Type & Target */}
                    {submission.submission_type === 'contribution' && submission.target_crt && (
                      <div className="mb-3">
                        <strong>Contributing to:</strong>{' '}
                        <a href={`/crts/${submission.target_crt.slug}`} target="_blank" rel="noopener noreferrer">
                          {submission.target_crt.title}
                        </a>
                      </div>
                    )}

                    {/* Author Information */}
                    <div className="mb-3">
                      <strong>Submitted by:</strong>{' '}
                      {typeof submission.author === 'object' 
                        ? submission.author.author_name || submission.author.display_name || 'Anonymous'
                        : submission.author || 'Anonymous'
                      }
                      <br />
                      <strong>Email:</strong>{' '}
                      {typeof submission.author === 'object' 
                        ? submission.author.email || 'N/A'
                        : 'N/A'
                      }
                      <br />
                      <strong>Date:</strong>{' '}
                      {new Date(submission.created_at).toLocaleString()}
                    </div>

                    {/* CRT Details */}
                    {submission.crt_data && (
                      <div className="mb-3">
                        <h6>CRT Details:</h6>
                        {submission.submission_type === 'contribution' && submission.target_crt && (
                          <div className="alert alert-info mb-3">
                            <h6 className="mb-3">Field Comparison</h6>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered">
                                <thead>
                                  <tr>
                                    <th>Field</th>
                                    <th>Current Value</th>
                                    <th>Submitted Value</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(submission.crt_data).map(([field, newValue]) => {
                                    const currentValue = submission.target_crt[field];
                                    const hasChange = JSON.stringify(currentValue) !== JSON.stringify(newValue);
                                    
                                    // Helper function to format value for display
                                    const formatValue = (value) => {
                                      if (Array.isArray(value)) {
                                        return value.map(item => typeof item === 'object' && item !== null ? JSON.stringify(item, null, 2).replace(/[{}\[\]"]/g, '').trim() : String(item)).join(', ');
                                      } else if (typeof value === 'object' && value !== null) {
                                        try {
                                          return JSON.stringify(value, null, 2).replace(/[{}\[\]"]/g, '').trim();
                                        } catch (e) {
                                          return String(value);
                                        }
                                      }
                                      return String(value || '');
                                    };
                                    
                                    return (
                                      <tr key={field}>
                                        <td><strong>{field}</strong></td>
                                        <td className="text-muted">
                                          {formatValue(currentValue)}
                                        </td>
                                        <td className="text-success">
                                          {formatValue(newValue)}
                                        </td>
                                        <td>
                                          <div className="form-check">
                                            <input
                                              type="checkbox"
                                              className="form-check-input"
                                              id={`field-${submission._id}-${field}`}
                                              checked={submissionFields[submission._id]?.has(field) || false}
                                              onChange={(e) => {
                                                console.log(`Checkbox changed for field ${field}:`, e.target.checked);
                                                handleFieldSelection(submission._id, field, e.target.checked);
                                              }}
                                            />
                                            <label className="form-check-label" htmlFor={`field-${submission._id}-${field}`}>
                                              Update
                                            </label>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        <div className="small">
                          {submission.crt_data.chassis && (
                            <div><strong>Chassis:</strong> {submission.crt_data.chassis}</div>
                          )}
                          {submission.crt_data.viewable_size && (
                            <div><strong>Size:</strong> {submission.crt_data.viewable_size}"</div>
                          )}
                          {submission.crt_data.chassis_stereo && (
                            <div><strong>Chassis Stereo:</strong> {submission.crt_data.chassis_stereo}</div>
                          )}
                          {submission.crt_data.chroma && (
                            <div><strong>Chroma:</strong> {submission.crt_data.chroma}</div>
                          )}
                          {submission.crt_data.osd && (
                            <div><strong>OSD:</strong> {submission.crt_data.osd}</div>
                          )}
                          {submission.crt_data.inputs && submission.crt_data.inputs.length > 0 && (
                            <div><strong>Inputs:</strong> {submission.crt_data.inputs.join(', ')}</div>
                          )}
                          {submission.crt_data.tubes && submission.crt_data.tubes.length > 0 && (
                            <div><strong>Tubes:</strong> {submission.crt_data.tubes.join(', ')}</div>
                          )}
                          {submission.crt_data.notes && (
                            <div>
                              <strong>Notes:</strong>
                              <div className="text-muted">{submission.crt_data.notes}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Image Gallery */}
                    {submission.images && submission.images.length > 0 && (
                      <div className="mb-3">
                        <h6>Submitted Images:</h6>
                        <Row className="g-2">
                          {submission.images.map((image, idx) => (
                            <Col key={idx} xs="auto">
                              <div className="position-relative">
                                <img
                                  src={image.cropped_url || image.compressed_url || image.url}
                                  alt={`Submission ${idx + 1}`}
                                  className="img-fluid rounded"
                                  style={{ 
                                    width: '150px', 
                                    height: '150px', 
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                />
                                <a
                                  href={image.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="position-absolute top-0 end-0 m-1"
                                >
                                  <Badge bg="dark">Full Size</Badge>
                                </a>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleSubmissionReview(submission, true)}
                        disabled={loading}
                      >
                        <FaCheck className="me-1" />
                        {submission.submission_type === 'contribution' ? 'Approve Contribution' : 'Approve New CRT'}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleSubmissionReview(submission, false)}
                        disabled={loading}
                      >
                        Reject
                      </Button>
                      {submission.submission_type === 'contribution' && submission.target_crt && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          href={`/crts/${submission.target_crt.slug}`}
                          target="_blank"
                        >
                          View Original CRT
                        </Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate(`/edit-product/${submission._id}`)}
                      >
                        <FaEdit /> Edit
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Store Changes Section */}
      <div className="admin-section mb-4">
        <h5><i className="fas fa-store me-2"></i>Store Changes</h5>
        {loading ? (
          <p>Loading...</p>
        ) : storeChanges.length === 0 ? (
          <p>No pending store changes to review.</p>
        ) : (
          <ul className="list-group">
            {storeChanges.map((change) => (
              <li key={change._id} className="list-group-item">
                <strong>Store:</strong>{' '}
                <a
                  href={`${ELECTRON_URL}/${change.store_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {change.store_handle || 'N/A'}
                </a>
                <br />
                <strong>Store ID:</strong> {change.store_id || 'N/A'}
                <br />
                <strong>Change Type:</strong> {change.updated_fields?.join(', ')}
                <br />
                <strong>Details:</strong>
                <pre>{change.details}</pre>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success"
                    onClick={() => handleReviewChanges(change._id)}
                    disabled={loading}
                  >
                    Mark as Reviewed
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/settings`)}
                  >
                    <FaEdit className="me-1" />Edit Store
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Product Changes Section */}
      <div className="admin-section mb-4">
        <h5><i className="fas fa-box me-2"></i>Product Changes</h5>
        {loading ? (
          <p>Loading...</p>
        ) : productChanges.length === 0 ? (
          <p>No pending product changes to review.</p>
        ) : (
          <div className="product-changes-container">
            {Object.values(groupedProductChanges).map((productGroup) => {
              const isExpanded = expandedProductIds[productGroup.productId];
              const summary = createChangeSummary(productGroup.changes);
              
              return (
                <Card key={productGroup.productId} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <a
                        href={`${ELECTRON_URL}/${productGroup.storeHandle}/product/${productGroup.productId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="me-2"
                      >
                        <strong>{productGroup.productName}</strong>
                      </a>
                      <Badge bg="info" className="me-2">
                        {productGroup.changes.length} change{productGroup.changes.length !== 1 ? 's' : ''}
                      </Badge>
                      <a
                        href={`${ELECTRON_URL}/${productGroup.storeHandle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted"
                      >
                        {productGroup.storeHandle}
                      </a>
                      
                      {/* Show mini thumbnails directly in header */}
                      <div className="d-inline-block ms-3">
                        {productGroup.allImageFields && Object.keys(productGroup.allImageFields).length > 0 ? (
                          (() => {
                            // Deduplicate by URL to avoid showing the same image multiple times
                            const uniqueUrls = [];
                            const seenUrls = new Set();
                            
                            Object.values(productGroup.allImageFields).forEach(url => {
                              if (!seenUrls.has(url)) {
                                uniqueUrls.push(url);
                                seenUrls.add(url);
                              }
                            });
                            
                            return (
                              <>
                                {uniqueUrls.slice(0, 3).map((url, idx) => (
                                  <Image 
                                    key={idx}
                                    src={url}
                                    className="ms-1 border"
                                    style={{ 
                                      height: '30px', 
                                      width: '30px', 
                                      objectFit: 'cover', 
                                      borderRadius: '4px' 
                                    }}
                                    onError={(e) => {
                                      console.log('Image failed to load:', url);
                                      e.target.src = '/placeholder.gif';
                                    }}
                                  />
                                ))}
                                {uniqueUrls.length > 3 && (
                                  <Badge bg="secondary" className="ms-1">
                                    +{uniqueUrls.length - 3} more
                                  </Badge>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          // Fallback: Extract images from the first change if no allImageFields
                          // This ensures we always have thumbnails even if grouping failed
                          (() => {
                            const firstChange = productGroup.changes[0];
                            const extractedFields = firstChange ? extractImageFields(firstChange) : {};
                            
                            // Deduplicate by URL
                            const uniqueUrls = [];
                            const seenUrls = new Set();
                            
                            Object.values(extractedFields).forEach(url => {
                              if (!seenUrls.has(url)) {
                                uniqueUrls.push(url);
                                seenUrls.add(url);
                              }
                            });
                            
                            return uniqueUrls.length > 0 ? (
                              <>
                                {uniqueUrls.slice(0, 3).map((url, idx) => (
                                  <Image 
                                    key={idx}
                                    src={url}
                                    className="ms-1 border"
                                    style={{ 
                                      height: '30px', 
                                      width: '30px', 
                                      objectFit: 'cover', 
                                      borderRadius: '4px' 
                                    }}
                                    onError={(e) => {
                                      e.target.src = '/placeholder.gif';
                                    }}
                                  />
                                ))}
                                {uniqueUrls.length > 3 && (
                                  <Badge bg="secondary" className="ms-1">
                                    +{uniqueUrls.length - 3} more
                                  </Badge>
                                )}
                              </>
                            ) : null;
                          })()
                        )}
                      </div>
                    </div>
                    <div>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => toggleProductExpanded(productGroup.productId)}
                      >
                        {isExpanded ? <FaCompressAlt /> : <FaExpandAlt />}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/edit-product/${productGroup.productId}`)}
                      >
                        <FaEdit /> Edit
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleReviewChanges(productGroup.changeIds)}
                        disabled={loading}
                      >
                        <FaCheck /> Mark All as Reviewed
                      </Button>
                    </div>
                  </Card.Header>
                  
                  <Card.Body>
                    {/* Image Preview Section - Always visible before details */}
                    {productGroup.allImageFields && Object.keys(productGroup.allImageFields).length > 0 ? (
                      <div className="image-preview-section mb-4 rounded">
                        <h6><FaImage className="me-1" /> Image Changes:</h6>
                        <div className="small text-muted mb-2">
                          {summary.actions.includes('create') 
                            ? "New product with these images:" 
                            : "Only showing new or modified images:"}
                        </div>
                        <Row>
                          {(() => {
                            // Deduplicate by URL, not by field name
                            const uniqueUrls = new Map();
                            Object.entries(productGroup.allImageFields).forEach(([field, url]) => {
                              if (!uniqueUrls.has(url)) {
                                uniqueUrls.set(url, { field, url });
                              }
                            });
                            
                            return Array.from(uniqueUrls.values()).map(({field, url}, index) => (
                              <Col key={index} xs={6} md={4} lg={3} xl={2} className="mb-3">
                                <Card className="h-100 preview-card">
                                  <div className="position-absolute" style={{ top: '5px', right: '5px' }}>
                                    <Badge bg="success" className="p-1 small">NEW</Badge>
                                  </div>
                                  <Card.Img 
                                    variant="top" 
                                    src={url} 
                                    style={{ height: '120px', objectFit: 'contain', padding: '10px' }}
                                    onError={(e) => {
                                      e.target.src = '/placeholder.gif';
                                    }}
                                  />
                                  {/* Only show field name on hover or special cases */}
                                  <div className="position-absolute w-100 h-100 top-0 start-0 d-flex justify-content-center align-items-center image-overlay" 
                                      style={{ background: 'rgba(0,0,0,0.5)', opacity: 0, transition: 'opacity 0.2s', borderRadius: 'calc(0.375rem - 1px)' }}>
                                    <div className="text-white p-2 text-center">
                                      <small>{field}</small>
                                    </div>
                                  </div>
                                </Card>
                              </Col>
                            ));
                          })()}
                        </Row>
                      </div>
                    ) : summary.images.length > 0 && (
                      <div className="image-preview-section mb-4 p-2 rounded">
                        <h6><FaImage className="me-1" /> Image Changes:</h6>
                        <div className="small text-muted mb-2">
                          {summary.actions.includes('create') 
                            ? "New product with these images:" 
                            : "Only showing new or modified images:"}
                        </div>
                        <Row>
                          {(() => {
                            // Deduplicate by URL, not by field name
                            const uniqueUrls = new Map();
                            summary.images.forEach(image => {
                              if (!uniqueUrls.has(image.url)) {
                                uniqueUrls.set(image.url, image);
                              }
                            });
                            
                            return Array.from(uniqueUrls.values()).map((image, index) => (
                              <Col key={index} xs={6} md={4} lg={3} xl={2} className="mb-3">
                                <Card className="h-100 preview-card">
                                  <div className="position-absolute" style={{ top: '5px', right: '5px' }}>
                                    <Badge bg="success" className="p-1 small">NEW</Badge>
                                  </div>
                                  <Card.Img 
                                    variant="top" 
                                    src={image.url} 
                                    style={{ height: '120px', objectFit: 'contain', padding: '10px' }}
                                    onError={(e) => {
                                      e.target.src = '/placeholder.gif';
                                    }}
                                  />
                                  {/* Only show field name on hover */}
                                  <div className="position-absolute w-100 h-100 top-0 start-0 d-flex justify-content-center align-items-center image-overlay" 
                                      style={{ background: 'rgba(0,0,0,0.5)', opacity: 0, transition: 'opacity 0.2s', borderRadius: 'calc(0.375rem - 1px)' }}>
                                    <div className="text-white p-2 text-center">
                                      <small>{image.field}</small>
                                    </div>
                                  </div>
                                </Card>
                              </Col>
                            ));
                          })()}
                        </Row>
                      </div>
                    )}
                    
                    {/* Text Changes Summary - Show all text changes in main view */}
                    {(() => {
                      // Collect all text field changes from all changes in the group
                      const allTextChanges = [];
                      
                      // Keep track of changes for metadata
                      const changeMetadata = new Map();
                      
                      productGroup.changes.forEach(change => {
                        // Store metadata for this change
                        changeMetadata.set(change._id, {
                          date: new Date(change.created_at).toLocaleString()
                        });
                        
                        if (change.changes) {
                          Object.entries(change.changes).forEach(([field, value]) => {
                            // Skip image fields and array fields which are handled separately above
                            if (isImageField(field, value.new) || 
                                (Array.isArray(value.new) && value.new.length > 0 && 
                                 typeof value.new[0] === 'object' && 
                                 (value.new[0].url || value.new[0].cropped_url || value.new[0].compressed_url))) {
                              return;
                            }
                            
                            // Format the value changes for display
                            let oldVal = value.old === undefined ? '(empty)' : 
                              (typeof value.old === 'object' && value.old !== null) ? 
                                JSON.stringify(value.old) : String(value.old);
                            let newVal = value.new === undefined ? '(empty)' : 
                              (typeof value.new === 'object' && value.new !== null) ? 
                                JSON.stringify(value.new) : String(value.new);
                            
                            // Truncate very long values
                            if (oldVal.length > 100) oldVal = oldVal.substring(0, 100) + '...';
                            if (newVal.length > 100) newVal = newVal.substring(0, 100) + '...';
                            
                            allTextChanges.push({
                              field,
                              oldVal,
                              newVal,
                              changeId: change._id
                            });
                          });
                        }
                      });
                      
                      if (allTextChanges.length === 0) return null;
                      
                      return (
                        <div className="text-changes-section mb-4">
                          <h6><FaCommentDots className="me-1" /> Text Changes:</h6>
                          <div className="table-responsive">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Old Value</th>
                                  <th>New Value</th>
                                  <th>When Changed</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allTextChanges.map((change, index) => {
                                  const metadata = changeMetadata.get(change.changeId) || {};
                                  return (
                                    <tr key={index}>
                                      <td><Badge bg="secondary">{change.field}</Badge></td>
                                      <td className="text-muted">{change.oldVal}</td>
                                      <td className="fw-bold">{change.newVal}</td>
                                      <td>
                                        <small className="text-muted">
                                          {metadata.date}
                                        </small>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {isExpanded && (
                      <Accordion defaultActiveKey="0" className="mt-3">
                        {productGroup.changes.map((change, index) => (
                          <Accordion.Item eventKey={index.toString()} key={change._id}>
                            <Accordion.Header>
                              <span>
                                Change {index + 1}: {change.updated_fields?.join(', ') || 'Unspecified Change'}
                              </span>
                            </Accordion.Header>
                            <Accordion.Body>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleReviewChanges(change._id)}
                                disabled={loading}
                                className="mt-0"
                              >
                                Mark Only This Change as Reviewed
                              </Button>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SuperAdmin;
